export interface Place {
  address: string | null;
  lat: number | null;
  lng: number | null;
}

const DECIMAL_COORDS = /(-?\d{1,2}[.,]\d{3,})\s*[,;/ ]\s*(-?\d{1,3}[.,]\d{3,})/;
const DMS_COORDS = /\d{1,3}\s*°\s*\d{1,2}\s*['′]\s*[\d.,]*\s*["″]?\s*[NSEOW]\b/gi;

/** Coordonnées GPS écrites dans le texte de l'adresse (l'ancienne application les y mettait parfois). */
export function coordsInText(text: string | null): { lat: number; lng: number } | null {
  const m = text?.match(DECIMAL_COORDS);
  if (!m) return null;
  const lat = Number(m[1]!.replace(",", "."));
  const lng = Number(m[2]!.replace(",", "."));
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
}

/** Adresse à afficher : sans coordonnées GPS ni mention « GPS : … ». Vide → null. */
export function cleanAddress(address: string | null): string | null {
  if (!address) return null;
  const cleaned = address
    .replace(new RegExp(DECIMAL_COORDS.source, "g"), " ")
    .replace(DMS_COORDS, " ")
    .replace(/\b(gps|coordonn[ée]es(\s+gps)?)\s*:?/gi, " ")
    .replace(/\(\s*\)/g, " ")
    .split("\n")
    .map((line) => line.replace(/\s{2,}/g, " ").replace(/^[\s,;:–-]+|[\s,;:–-]+$/g, ""))
    .filter(Boolean)
    .join("\n");
  return cleaned || null;
}

/** Lieu pour l'itinéraire : coordonnées enregistrées, sinon celles trouvées dans l'adresse, sinon l'adresse. */
export function placeOf(p: Place): Place {
  if (p.lat !== null && p.lng !== null) return { address: cleanAddress(p.address), lat: p.lat, lng: p.lng };
  const found = coordsInText(p.address);
  return { address: cleanAddress(p.address), lat: found?.lat ?? null, lng: found?.lng ?? null };
}

export function hasPlace(p: Place): boolean {
  return Boolean(p.address?.trim()) || (p.lat !== null && p.lng !== null);
}

function query(p: Place): string {
  if (p.lat !== null && p.lng !== null) return `${p.lat},${p.lng}`;
  return p.address ?? "";
}

/** Liens d'itinéraire vers les applications de navigation installées. */
export function navigationLinks(p: Place): Array<{ id: string; label: string; href: string }> {
  const q = encodeURIComponent(query(p));
  const links = [
    { id: "google", label: "Google Maps", href: `https://www.google.com/maps/dir/?api=1&destination=${q}` },
    {
      id: "waze",
      label: "Waze",
      href:
        p.lat !== null && p.lng !== null
          ? `https://waze.com/ul?ll=${p.lat},${p.lng}&navigate=yes`
          : `https://waze.com/ul?q=${q}&navigate=yes`
    }
  ];
  if (/iPhone|iPad|Macintosh/.test(navigator.userAgent)) {
    links.push({ id: "apple", label: "Plans (Apple)", href: `https://maps.apple.com/?daddr=${q}` });
  }
  return links;
}
